import { type ReactElement } from "react";
import { Link, Outlet, useNavigate, useParams } from "react-router-dom";
import { usePortal, findService, findStage } from "../state/PortalContext.js";
import { stageMemory } from "../lib/display.js";

const COLOR_HEX: Record<string, string> = {
  red: "#b3261e",
  amber: "#9a6700",
  blue: "#0b57d0",
  green: "#1e7d34",
  grey: "#5f6368",
};

/** Top bar: service switcher + stage dropdown (coloured by profile), protected banner, module nav. */
export function AppLayout(): ReactElement {
  const { me, catalog, productName } = usePortal();
  const params = useParams();
  const navigate = useNavigate();

  const serviceId = params.service ?? catalog[0]?.serviceId ?? "";
  const service = findService(catalog, serviceId);
  const stageId = params.stage ?? service?.stages[0]?.id ?? "";
  const stage = findStage(catalog, serviceId, stageId);

  function goto(nextService: string, nextStage: string, moduleId?: string): void {
    stageMemory.set(nextService, nextStage);
    const svc = findService(catalog, nextService);
    const mod = moduleId ?? svc?.stages.find((s) => s.id === nextStage)?.modules[0]?.id ?? "";
    navigate(`/${nextService}/${nextStage}/${mod}`);
  }

  return (
    <div className="layout">
      <header className="topbar" style={{ borderBottom: `4px solid ${COLOR_HEX[stage?.color ?? "grey"]}` }}>
        <span className="brand">{productName}</span>

        <label className="sr-only" htmlFor="service-switcher">
          Service
        </label>
        <select
          id="service-switcher"
          title="The app you are managing. You only see apps where you have been given a role."
          value={serviceId}
          onChange={(e) => {
            const svc = findService(catalog, e.target.value);
            const remembered = stageMemory.get(e.target.value);
            const nextStage =
              (remembered && svc?.stages.some((s) => s.id === remembered) ? remembered : svc?.stages[0]?.id) ?? "";
            goto(e.target.value, nextStage);
          }}
        >
          {catalog.map((s) => (
            <option key={s.serviceId} value={s.serviceId}>
              {s.displayName}
            </option>
          ))}
        </select>

        <label className="sr-only" htmlFor="stage-dropdown">
          Stage
        </label>
        <select
          id="stage-dropdown"
          title="The environment you are working in. Red is live (real members); amber is a test copy. Everything you see and change applies only to this one."
          value={stageId}
          style={{ color: COLOR_HEX[stage?.color ?? "grey"] }}
          onChange={(e) => goto(serviceId, e.target.value, params.module)}
        >
          {(service?.stages ?? []).map((s) => (
            <option key={s.id} value={s.id} style={{ color: COLOR_HEX[s.color] }}>
              {s.label}
            </option>
          ))}
        </select>

        <nav className="module-nav" aria-label="Modules">
          {(stage?.modules ?? []).map((m) => (
            <Link key={m.id} to={`/${serviceId}/${stageId}/${m.id}`} title={m.description}>
              {m.displayName ?? m.id}
            </Link>
          ))}
          {(stage?.features ?? []).map((f) => (
            <Link
              key={`f-${f.id}`}
              to={`/${serviceId}/${stageId}/f/${f.id}`}
            >
              {f.displayName ?? f.id}
            </Link>
          ))}
          {stage?.ml ? (
            <Link to={`/${serviceId}/${stageId}/_models`} title="Train, check and switch on the scoring models that help reviewers.">
              Models
            </Link>
          ) : null}
          <Link to={`/${serviceId}/${stageId}/_intelligence`} title="Discovery policy, fair exposure boosts, and A/B experiments.">
            Discovery Policy
          </Link>
          <Link to={`/${serviceId}/${stageId}/_link`} title={`Prove which ${service?.displayName ?? serviceId} account is yours. Optional; gives no extra access.`}>
            Link identity
          </Link>
          {me.superAdmin ? (
            <Link to="/_admins" title="Give or remove roles for other admins.">
              Admins
            </Link>
          ) : null}
        </nav>

        <span className="admin-email" title="The account you are signed in as">
          {me.email}
        </span>
      </header>

      {stage?.protected ? (
        <div className="protected-banner" role="status" style={{ background: COLOR_HEX[stage.color] }}>
          Protected stage: {stage.label}. Changes here affect real members. Risky actions ask you to type the
          stage name to confirm.
        </div>
      ) : null}

      <main className="content">
        <Outlet />
      </main>
    </div>
  );
}
